import { useCallback, useEffect, useMemo, useState } from "react";
import { Row, Col, Button, Card, Nav, ProgressBar } from "react-bootstrap";
import useSWR, { mutate } from "swr";
import AuthenticatedWrapper from "../../components/wrapper";
import variables from "../../styles/utils.module.scss";
import {
  AWS_S3_BASE_URL,
  fetcher,
  getIconType,
  getParameterByName,
  scrollTop,
  serverUrl,
  swrConfig,
  uploadPresignedFile,
} from "../../helpers/utility";
import { useForm } from "react-hook-form";
import axios from "axios";
import InputWithLabel from "../../components/inputWithLabel";
import { getGeocodeByAddress } from "../../helpers/maps";
import CheckboxWithLabel from "../../components/CheckboxWithLabel";
import { getFiltersToSave, supportNeeds } from "../../helpers/supportNeeds";
import { useNavigate, useSearchParams } from "react-router-dom";
import { GENDER, INVALID_VALUE, TYPE_OF_CARE } from "../../helpers/constants";
import { format, isToday } from "date-fns";
import NavigationBack from "../../components/NavigationBack";
import BasicModal from "../../components/BasicModal";
import InputWithSelect from "../../components/inputWithSelect";
import InputReadOnly from "../../components/inputReadOnly";
import styles from '../../styles/addLocation.module.scss';

function getGenderItemKey(key) {
  return `gender-${key}`;
}

function getTypeOfCareItemKey(key) {
  return `typeOfCare-${key}`;
}

// Note: We comvert from yers to months when saving
function getAgeToSave(data) {
  const minimum = data.minAgeInYears * 12 || 0;
  const maximum = data.maxAgeInYears * 12 || 99;

  delete data.minAgeInYears;
  delete data.maxAgeInYears;

  return {
    minimum,
    maximum,
  };
}

function getGendersToSave(data) {
  const keys = Object.keys(GENDER);
  let genders = [];

  // Loop over support needs KEYS and map to the form DATA value
  keys.forEach((item) => {
    if (data[getGenderItemKey(item)] === true) {
      genders.push(item);
    }
  });

  return genders;
}

function getTypeOfCareToSave(data) {
  // For single select dropdown, return array with single value if selected
  if (data.typeOfCare && data.typeOfCare !== "-1") {
    console.log("Saving typeOfCare:", data.typeOfCare);
    return [data.typeOfCare];
  }
  return [];
}

function AddLocation({ user }) {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
    setError,
    clearErrors,
    trigger,
  } = useForm();
  const formValues = watch();
  const navigate = useNavigate();
  const [queryParameters, setSearchParams] = useSearchParams();
  const [fileUpload, setFileUpload] = useState(null);
  const [galleryURLs, setGalleryURLs] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalConfig, setModalConfig] = useState(null);
  const locationId = queryParameters.get("locationId");
  const initialStep = queryParameters.get("initialStep");
  const back = queryParameters.get("back");
  const steps = {
    INFORMATION: "INFORMATION",
    SUPPORT: "SUPPORT",
    GALLERY: "GALLERY",
    DOCUMENT: "DOCUMENT",
    AVAILABILITY: "AVAILABILITY",
    PAYMENT: "PAYMENT",
    CONFIRMATION: "CONFIRMATION",
  };
  const [step, setStep] = useState(initialStep || steps.INFORMATION);
  const needs = { low: "low", medium: "medium", high: "high" };
  const [showSupportNeeds, setShowSupportNeeds] = useState(false);
  const [createdByUser, setCreatedByUser] = useState(null);
  const [locationStatus, setLocationStatus] = useState(null);
  const isLocalAuthority = user?.localAuthorityId;
  let locationData = {},
    locationError;

  const { data, error } = useSWR(
    `${serverUrl}/location/id/${locationId || INVALID_VALUE}`,
    fetcher,
    swrConfig
  );
  console.log(data);
  locationData = data;
  locationError = error;

  const IN_REVIEW = "IN_REVIEW";
  const APPROVED = "APPROVED";
  const REJECTED = "REJECTED";

  useEffect(() => {
    async function fetchCreatedByUser({ userId }) {
      if (userId && user?.staffUser) {
        const userInfo = await axios.get(`${serverUrl}/admin/user/info/${userId}`);

        console.log(JSON.stringify(userInfo.data));
        if (userInfo.data.user) {
          setCreatedByUser(userInfo.data.user);
        }
      }
    }

    if (locationData?.location) {
      setValue("name", locationData.location.name);
      setValue("description", locationData.location.description);
      setValue("website", locationData.location.website);
      setValue("email", locationData.location.email);
      setValue("phone", locationData.location.phone);
      setValue("ofstedId", locationData.location.ofstedId);
      setValue("staffCount", locationData.location.staffCount);
      setValue("staffPersonInCharge", locationData.location.staffPersonInCharge);
      setValue("addressLine1", locationData.location.address?.addressLine1);
      setValue("addressTownCity", locationData.location.address?.townCity);
      setValue("addressPostcode", locationData.location.address?.postcode);
      setValue("capacityMax", locationData.location.capacityMax);
      setValue("capacityUsed", locationData.location.capacityUsed);
      setGalleryURLs(locationData.location.galleryURLs || []);
      setDocuments(locationData.location.documents || []);

      // Preload locationData.location.galleryURLs
      if (locationData.location.galleryURLs?.length > 0) {
        locationData.location.galleryURLs.forEach((image) => {
          const url = `${AWS_S3_BASE_URL}/${image.externalId}`;
          new Image().src = url;
        });
      }

      if (locationData.location.ageRangeInMonths?.length > 0) {
        if (locationData.location.ageRangeInMonths[0].minimum > 0) {
          setValue("minAgeInYears", locationData.location.ageRangeInMonths[0].minimum / 12);
        } else {
          setValue("minAgeInYears", 0);
        }
        if (locationData.location.ageRangeInMonths[0].maximum > 0) {
          setValue("maxAgeInYears", locationData.location.ageRangeInMonths[0].maximum / 12);
        }
      }

      // Special: Location status
      setLocationStatus(locationData.location.status);

      // Special parse filters
      if (
        locationData.location?.filters &&
        Object.keys(locationData.location.filters)?.length > 0
      ) {
        Object.keys(locationData.location.filters).forEach((filterKey) => {
          setValue(filterKey, locationData.location.filters[filterKey]);
        });
      }

      if (locationData.location.createdBy) {
        fetchCreatedByUser({ userId: locationData.location.createdBy });
      }

      if (locationData.location?.genders && locationData.location.genders?.length > 0) {
        locationData.location.genders.forEach((key) => {
          setValue(getGenderItemKey(key), true);
        });
      }

      if (locationData.location?.typeOfCare && locationData.location.typeOfCare?.length > 0) {
        // For single select, set the first (and only) value
        const typeOfCareValue = locationData.location.typeOfCare[0];
        console.log("Setting typeOfCare value:", typeOfCareValue);
        setValue("typeOfCare", typeOfCareValue);
      } else {
        // Set default value to the placeholder
        console.log("Setting typeOfCare value to placeholder");
        setValue("typeOfCare", "-1");
      }
    }
  }, [locationData]);

  function handleSelectedGalleryImage(event) {
    setFileUpload({
      selectedFiles: event.target.files,
      filesToUpload: event.target.files.length,
      filesUploaded: 0,
      loaded: 0,
      type: "GALLERY",
    });
  }

  function handleSelectedDocument(event) {
    setFileUpload({
      selectedFiles: event.target.files,
      filesToUpload: event.target.files.length,
      filesUploaded: 0,
      loaded: 0,
      type: "DOCUMENT",
    });
  }

  // Auto start the upload
  useEffect(() => {
    if (fileUpload && fileUpload?.selectedFiles?.length > 0) {
      if (fileUpload?.type === "GALLERY") {
        console.log(`Gallery upload started...`);
        handleGalleryUpload();
      }
      if (fileUpload?.type === "DOCUMENT") {
        console.log(`Document upload started...`);
        handleDocumentUpload();
      }
    }
  }, [fileUpload]);

  // Handle upload directly to Amazon S3 Bucket
  function handleGalleryUpload() {
    const requestAWSUpload = `${serverUrl}/fileUpload/presignedUrl`;
    const newGallery = [...galleryURLs];
    let uploadCount = fileUpload.selectedFiles?.length,
      completionCount = 0;

    for (let fileObject of fileUpload.selectedFiles) {
      const fileMeta = {
        name: fileObject.name,
        size: fileObject.size,
        type: fileObject.type,
        acl: "public-read",
        folder: "location-media",
      };

      if (fileObject.lastModified) {
        fileMeta.lastModified = new Date(fileObject.lastModified);
      }

      // Tagging as raw flags to AWS that we can archive as infrequently accessed (50% cheaper)
      fileMeta.tagObject = "raw";

      axios
        .post(requestAWSUpload, fileMeta)
        .then((res) => {
          if (res && res.data && res.data.key && res.data.contentType && res.data.url) {
            uploadPresignedFile({
              presignedurl: res.data.url,
              file: fileObject,
              acl: fileMeta?.acl,
              onSuccess: () => {
                fileMeta.externalId = res.data.key;
                fileMeta.contentType = res.data.contentType;
                newGallery.push(fileMeta);
                completionCount++;

                if (completionCount === uploadCount) {
                  setGalleryURLs(newGallery);
                }
              },
              onFail: null,
              onProgress: (ProgressEvent) => {
                // TODO
              },
            });
          }
        })
        .catch((error) => {
          if (error && error.response?.data?.errorCode === "TOO_LARGE") {
            alert(
              `Please upload smaller files so that your listing can be loaded quickly. The maximum size for images: 6MB, videos: 50MB.`
            );
          }
        });
    }
  }

  // Handle upload directly to Amazon S3 Bucket
  function handleDocumentUpload() {
    const requestAWSUpload = `${serverUrl}/fileUpload/presignedUrl`;
    const newDocuments = [...documents];
    let uploadCount = fileUpload.selectedFiles?.length,
      completionCount = 0;

    for (let fileObject of fileUpload.selectedFiles) {
      const fileMeta = {
        name: fileObject.name,
        size: fileObject.size,
        type: fileObject.type,
        acl: "private",
        folder: "location-documents",
      };

      if (fileObject.lastModified) {
        fileMeta.lastModified = new Date(fileObject.lastModified);
      }

      // Tagging as raw flags to AWS that we can archive as infrequently accessed (50% cheaper)
      fileMeta.tagObject = "raw";

      axios
        .post(requestAWSUpload, fileMeta)
        .then((res) => {
          if (res && res.data && res.data.key && res.data.contentType && res.data.url) {
            uploadPresignedFile({
              presignedurl: res.data.url,
              file: fileObject,
              acl: fileMeta?.acl,
              onSuccess: () => {
                fileMeta.externalId = res.data.key;
                fileMeta.contentType = res.data.contentType;
                newDocuments.push(fileMeta);
                completionCount++;

                if (completionCount === uploadCount) {
                  setDocuments(newDocuments);
                }
              },
              onFail: null,
              onProgress: (ProgressEvent) => {
                // TODO
              },
            });
          }
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }

  function hideModal() {
    setShowModal(false);
    setModalConfig(null);
  }

  async function nextStep() {
    clearErrors();

    // Save each 'tab' immediately if it's an existing location
    if (locationId) {
      handleSubmit(onSubmit)();
      return;
    }

    if (step === steps.INFORMATION) {
      // Note: Can use array of field names instead of validating 'everything'
      let validationOK = await trigger([
        "name",
        "website",
        "email",
        "addressLine1",
        "addressPostcode",
        "staffPersonInCharge",
        "staffCount",
        "typeOfCare",
      ]);

      if (validationOK !== true) {
        return false;
      }

      setStep(steps.SUPPORT);
    } else if (step === steps.SUPPORT) {
      setStep(steps.GALLERY);
    } else if (step === steps.GALLERY) {
      setStep(steps.DOCUMENT);
    } else if (step === steps.DOCUMENT) {
      setStep(steps.AVAILABILITY);
    } else if (step === steps.AVAILABILITY) {
      let validationOK = await trigger(["capacityMax", "capacityUsed"]);

      if (validationOK !== true) {
        return false;
      }

      // Wait for the location to be saved before proceeding to payment
      // Use a flag to track if save was successful
      // let saveSuccessful = false;
      
      // const submitHandler = handleSubmit(
      //   async (data) => {
      //     const result = await onSubmit(data);
      //     if (result === true) {
      //       saveSuccessful = true;
      //     }
      //   },
      //   () => {
      //     // Validation failed - don't proceed
      //     saveSuccessful = false;
      //   }
      // );
      
      // Execute the submit handler and wait for it to complete
      // await submitHandler();
      
      // Only proceed to payment if save was successful
      // For new locations, onSubmit will update the URL with locationId via setSearchParams
      // and the component will re-render with the new locationId from URL params
      // For existing locations, proceed to payment step after successful save
      if (!locationData?.location?.subscriptionActive) {
        setStep(steps.CONFIRMATION);
      }
    }

    scrollTop();
  }

  async function saveAndSubmitLocation() {
    await onSubmit(formValues);

    setShowModal(true);
    setModalConfig({
      title:
        !locationId || locationId === "null" || locationId === "undefined"
          ? `Location Saved and Submitted`
          : `Location Updated and Submitted`,
      message:
        !locationId ||
        locationId === "null" ||
        locationId ===
          "undefined"
          ? `Your location has been saved and submitted for review. You will receive an email confirmation once it has been reviewed.`
          : `Your location has been updated.`,
    });

    setTimeout(() => {
      window.location.href = `${window.location.origin}/#/provider/dashboard`;
    }, 3000);
  }
  
  const goToListingCheckout = useCallback(async () => {
    // Validate locationId before proceeding
    if (!locationId || locationId === "null" || locationId === "undefined") {
      setModalConfig({
        title: `Location Required`,
        message: `Please save your location information first before proceeding to payment.`,
      });
      setShowModal(true);
      return;
    }

    try {
      const response = await axios.get(
        `${serverUrl}/checkout-session?email=${encodeURIComponent(
          user.email
        )}&locationId=${locationId}&promoCode=HALF6`
      );

      if (response) {
        if (response.data?.url) {
          console.log(`URL is: ${response.data.url}`);
          window.location.href = response.data.url;
        }
      }
    } catch (error) {
      console.error(`ERR: Stripe. ${error}`);
      setModalConfig({
        title: `Payment Error`,
        message: `There was an error initiating payment. Please try again or contact support if the problem persists.`,
      });
      setShowModal(true);
    }
  }, [locationId, user.email]);

  const cancelLocation = useCallback(async () => {
    try {
      const response = await axios.post(`${serverUrl}/stripe/cancel-subscription`, { locationId });

      if (response) {
        console.log(`Cancellation completed? ${response?.data}`);
        await mutate(`${serverUrl}/location/id/${locationId}`);

        setModalConfig({
          title: `Subscription Cancelled`,
          message: `Your monthly subscription has now been cancelled for this location. Once your subscription expires, this location will no longer be shown in our search results.`,
        });

        setShowModal(true);
      }
    } catch (error) {
      console.error(`ERR: Stripe. ${error}`);
    }
  }, [locationId]);

  async function onSubmit(data) {
    try {
      // Ensure ID is passed across so can update same document
      if (locationId) {
        data._id = locationId;
      }

      // Only re-calculate geocode address if has changed (or did not exist)
      if (
        !locationData?.location?.address ||
        locationData.location.address?.addressLine1 !== formValues.addressLine1 ||
        locationData.location.address?.addressPostcode !== formValues.addressPostcode
      ) {
        try {
          const addressGeocode = await getGeocodeByAddress({
            address: `${formValues.addressLine1}, ${formValues.addressTownCity}, ${formValues.addressPostcode}, `,
          });

          if (addressGeocode) {
            // Convert address
            const address = {
              addressLine1: data.addressLine1,
              //addressLine2:  data.addressLine1,
              townCity: data.addressTownCity,
              //county: data.addressLine1,
              postcode: data.addressPostcode,
              // May not have changed, so use old value
              lat: addressGeocode.location?.lat,
              lng: addressGeocode.location?.lng,
            };

            data.address = address;
          }
        } catch (error) {
          console.error(`Failed to lookup address geocode. ${error}`);

          setModalConfig({
            title: `Invalid Address`,
            message: `We could not find that address, please check and try again.`,
          });

          setShowModal(true);
          return false;
        }
      }

      // Convert to numeric values for database
      if (typeof data.capacityUsed === "string") {
        data.capacityUsed = parseInt(data.capacityUsed);
      }
      if (typeof data.capacityMax === "string") {
        data.capacityMax = parseInt(data.capacityMax);
      }
      if (typeof data.staffCount === "string") {
        data.staffCount = parseInt(data.staffCount);
      }

      // Parse support filters for saving
      data.filters = getFiltersToSave(data);

      data.genders = getGendersToSave(data);

      data.typeOfCare = getTypeOfCareToSave(data);

      data.ageRangeInMonths = getAgeToSave(data);

      // Gallery / Documents
      data.galleryURLs = galleryURLs;
      data.documents = documents;

      // Updating course section
      console.log("About to send data to server:", data);
      console.log("typeOfCare in data:", data.typeOfCare);
      console.log("Server URL:", serverUrl);
      console.log("Full API URL:", `${serverUrl}/location`);
      
      let sectionResponse;
      try {
        sectionResponse = await axios.put(`${serverUrl}/location`, data);
        console.log("API call successful, response:", sectionResponse);
        console.log("Response data:", sectionResponse.data);
        console.log("Response data success:", sectionResponse.data?.success);
        console.log("Response data result:", sectionResponse.data?.result);
      } catch (error) {
        console.error("API call failed:", error);
        console.error("Error status:", error.response?.status);
        console.error("Error data:", error.response?.data);
        throw error;
      }

      if (sectionResponse && sectionResponse.data && sectionResponse.data.success) {
        console.log(`----- Updated section`);

        // Update saved
        if (locationId) {
          setModalConfig({
            title: `Location Updated`,
            message: `Your changes have been saved.`,
          });

          setShowModal(true);
          return true; // Return success for existing locations
        } else {
          // NEW location saved, redirect back to flow with locationId
          const newLocationId = sectionResponse.data?.result?._id;
          setSearchParams({
            initialStep: "PAYMENT",
            locationId: newLocationId,
          });
          return true; // Return success for new locations
        }
      } else {
        setModalConfig({
          title: `Save Error`,
          message: `We had a problem saving your changes, please check all fields and try again.`,
        });

        setShowModal(true);
        return false; // Return failure
      }
    } catch (error) {
      console.error(`Error submitting add-location form. ${error}`);
      setModalConfig({
        title: `Save Error`,
        message: `We had a problem saving your changes. Please try again.`,
      });
      setShowModal(true);
      return false; // Return failure on error
    }
  }

  function showGeneralForm() {
    return (
      <div className={styles.formSection}>
        <InputWithLabel
          inputId={`name`}
          label={`Name`}
          placeholder=""
          type="text"
          register={register}
          error={errors.name}
          errorMessage={`Required. Enter at least 3 characters.`}
          validation={{ required: true, minLength: 3 }}
        />
        <InputWithLabel
          inputId={`description`}
          label={`Description`}
          placeholder="A short description of your accomodation that will be shown in your listing."
          type="textarea"
          inputProps={{ rows: 3 }}
          register={register}
        />
        <InputWithLabel
          inputId={`website`}
          label={`Website`}
          placeholder="https://www..."
          type="text"
          register={register}
          error={errors.website}
          errorMessage={`Required. Enter your website.`}
          validation={{ required: true, minLength: 4 }}
        />
        <InputWithLabel
          inputId={`phone`}
          label={`Phone`}
          placeholder="+44"
          type="text"
          register={register}
        />
        <InputWithLabel
          inputId={`email`}
          label={`Enquiries Email Address`}
          placeholder=""
          type="text"
          register={register}
          error={errors.email}
          errorMessage={`Required. Enter your email address.`}
          validation={{ required: true, minLength: 3 }}
        />
        <InputWithLabel
          inputId={`ofstedId`}
          label={`Ofsted Unique Reference Number (URN)`}
          placeholder=""
          type="text"
          register={register}
          error={errors.ofstedId}
          errorMessage={`Optional. URN is usually 7 digits long.`}
          validation={{ required: false, minLength: 7, maxLength: 9 }}
        />

        {locationData?.location?.ofstedReport?.inspections?.length > 0 ? (
          <div className={styles.helpText}>
            Ofsted inspection:
            <a
              href={locationData.location?.ofstedReport?.inspections[0]?.link}
              target="_blank"
              style={{ marginLeft: 4 }}
            >
              {locationData.location?.ofstedReport?.inspections[0]?.name}
            </a>
          </div>
        ) : null}

        <div className={styles.sectionHeader}>
          <h3 className="h5">Location</h3>
        </div>
        <InputWithLabel
          inputId={`addressLine1`}
          label={`Address Line 1`}
          placeholder=""
          type="text"
          register={register}
          error={errors.addressLine1}
          errorMessage={`Required. Enter at least 3 characters.`}
          validation={{ required: true, minLength: 3 }}
        />
        <InputWithLabel
          inputId={`addressTownCity`}
          label={`Town / City`}
          placeholder=""
          type="text"
          register={register}
        />
        <InputWithLabel
          inputId={`addressPostcode`}
          label={`Postcode`}
          placeholder=""
          type="text"
          register={register}
          error={errors.addressPostcode}
          errorMessage={`Required. Enter at least 6 characters.`}
          validation={{ required: true, minLength: 6 }}
        />

        <div className={styles.sectionHeader}>
          <h3 className="h5">Team</h3>
        </div>
        <InputWithLabel
          inputId={`staffPersonInCharge`}
          label={`Who is in charge of care?`}
          placeholder=""
          type="text"
          register={register}
          error={errors.staffPersonInCharge}
          errorMessage={`Required. Who is in charge of care?`}
          validation={{ required: true, minLength: 3 }}
        />

        <InputWithLabel
          inputId={`staffCount`}
          label={`How many staff do you employ in care roles?`}
          placeholder=""
          type="number"
          register={register}
          error={errors.staffCount}
          errorMessage={`Required.`}
          validation={{ required: true, min: 1 }}
        />
      </div>
    );
  }

  // Provider support capabilities
  function showSupportForm() {
    return (
      <div className={styles.formSection}>
        <div className={styles.sectionHeader}>
          <h3 className="h5">Age range</h3>
        </div>
        <div className={styles.formGroup}>
          <InputWithLabel
            inputId={`minAgeInYears`}
            label={`What is the minimum child age in years?`}
            placeholder=""
            type="number"
            register={register}
            error={errors.minAgeInMonths}
            errorMessage={`Required.`}
            validation={{ required: true, min: 0, max: 99 }}
          />

          <InputWithLabel
            inputId={`maxAgeInYears`}
            label={`What is the maximum child age in years?`}
            placeholder=""
            type="number"
            register={register}
            error={errors.maxAgeInMonths}
            errorMessage={`Required.`}
            validation={{ required: true, min: 0, max: 99 }}
          />
        </div>

        <div className={styles.sectionHeader}>
          <h3 className="h5">Type of Care</h3>
        </div>
        <div className={styles.formGroup}>
          <InputWithSelect
            inputId={`typeOfCare`}
            label={"Type of care you offer"}
            register={register}
            error={errors.typeOfCare}
            errorMessage={`Required. Type of care?`}
            validation={{ required: true, validate: (value) => value !== "-1" }}
            options={Object.keys(TYPE_OF_CARE).map((key) => {
              return { value: key, label: TYPE_OF_CARE[key] };
            })}
          />
        </div>

        <div className={styles.sectionHeader}>
          <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
            <h3 className="h5" style={{ marginRight: 5, marginBottom: 0 }}>
              Genders
            </h3>
            <label
              onClick={() => {
                Object.keys(GENDER).forEach((item) => {
                  setValue(getGenderItemKey(item), true);
                });
              }}
              className={styles.selectAllLink}
            >
              (Select All)
            </label>
          </div>
        </div>

        <div className={styles.checkboxSection}>
          {Object.keys(GENDER)?.length > 0
            ? Object.keys(GENDER)
                .sort()
                .map((item) => {
                  return (
                    <CheckboxWithLabel
                      inputId={getGenderItemKey(item)}
                      label={GENDER[item]}
                      type="checkbox"
                      register={register}
                      wrapperClass={"check-row-wrapper"}
                      inputProps={{ className: null }}
                      key={getGenderItemKey(item)}
                    />
                  );
                })
            : null}
        </div>

        <div className={styles.sectionHeader}>
          <h3 className="h5">Support Available</h3>
        </div>

        <div className={styles.helpText}>
          What kind of needs can you support at this location? Defining the support available
          helps Local Authorities quickly find the most suitable locations and increases the
          quality of enquiries that you receive.
        </div>

        {/* Support needs */}
        {Object.keys(supportNeeds)?.length > 0
          ? Object.keys(supportNeeds).map((supportNeedKey) => {
              const supportNeedsItem = supportNeeds[supportNeedKey];

              return (
                <div
                  style={{
                    marginBottom: 15,
                  }}
                  key={supportNeedsItem.key}
                >
                  <h1 className="h5" style={{ marginTop: 30 }}>
                    {supportNeedsItem.title}
                  </h1>

                  <div className={styles.checkboxSection}>
                    {Object.keys(supportNeedsItem.items)?.length > 0
                      ? Object.keys(supportNeedsItem.items)
                          .sort()
                          .map((risk) => {
                            return (
                              <CheckboxWithLabel
                                inputId={risk}
                                label={supportNeedsItem.items[risk]}
                                type="checkbox"
                                register={register}
                                wrapperClass={"check-row-wrapper"}
                                inputProps={{ className: null }}
                              />
                            );
                          })
                      : null}
                  </div>
                </div>
              );
            })
          : null}
      </div>
    );
  }

  async function deleteImage(id) {
    const success = await axios.delete(
      `${serverUrl}/location/image/${id}?locationId=${locationId}`
    );

    await mutate(`${serverUrl}/location/id/${locationId}`);
  }

  async function deleteDocument(id) {
    const success = await axios.delete(
      `${serverUrl}/location/document/${id}?locationId=${locationId}`
    );

    await mutate(`${serverUrl}/location/id/${locationId}`);
  }

  function showConfirmationForm() {
    // Helper to get selected genders
    const selectedGenders = Object.keys(GENDER).filter((key) => formValues[getGenderItemKey(key)] === true);
    const genderLabels = selectedGenders.map((key) => GENDER[key]);

    // Helper to get selected support needs
    const getSelectedSupportNeeds = () => {
      const selected = [];
      Object.keys(supportNeeds).forEach((categoryKey) => {
        const category = supportNeeds[categoryKey];
        const items = Object.keys(category.items).filter((itemKey) => formValues[itemKey] === true);
        if (items.length > 0) {
          selected.push({
            category: category.title,
            items: items.map((itemKey) => category.items[itemKey]),
          });
        }
      });
      return selected;
    };

    const selectedSupportNeeds = getSelectedSupportNeeds();

    // Helper to navigate to edit section
    const handleEditSection = (stepToNavigate) => {
      setStep(stepToNavigate);
      scrollTop();
    };

    // Helper component for section header with edit button
    const SectionHeaderWithEdit = ({ title, stepToNavigate }) => (
      <div className={styles.sectionHeader} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 className="h5">{title}</h3>
        <button
          type="button"
          onClick={() => handleEditSection(stepToNavigate)}
          className={styles.editButton}
        >
          <i className="bi bi-pencil"></i>
          <span>Edit</span>
        </button>
      </div>
    );

    return (
      <div className={styles.formSection}>
        <h1 className="h3">Thank you for choosing to list your location with bPlaced!</h1>
        <p>
          Please review your listing below and submit it for review. It can take up to 2 business days
          for it to be reviewed. Once approved, you will receive an email confirmation.
        </p>
        <p>
          If you have any questions, please contact us at{" "}
          <a href="mailto:info@bplaced.co.uk">info@bplaced.co.uk</a>
        </p>
        <div style={{ marginTop: 10 }}>
          <SectionHeaderWithEdit title="General Information" stepToNavigate={steps.INFORMATION} />

          <InputReadOnly label="Name" value={formValues.name || ""} />
          <InputReadOnly label="Description" type="textarea" value={formValues.description || ""} />
          <InputReadOnly label="Website" value={formValues.website || ""} />
          <InputReadOnly label="Phone" value={formValues.phone || ""} />
          <InputReadOnly label="Enquiries Email Address" value={formValues.email || ""} />
          <InputReadOnly
            label="Ofsted Unique Reference Number (URN)"
            value={formValues.ofstedId || ""}
          />

          <SectionHeaderWithEdit title="Location" stepToNavigate={steps.INFORMATION} />

          <InputReadOnly label="Address Line 1" value={formValues.addressLine1 || ""} />
          <InputReadOnly label="Town / City" value={formValues.addressTownCity || ""} />
          <InputReadOnly label="Postcode" value={formValues.addressPostcode || ""} />

          <SectionHeaderWithEdit title="Team" stepToNavigate={steps.INFORMATION} />

          <InputReadOnly
            label="Who is in charge of care?"
            value={formValues.staffPersonInCharge || ""}
          />
          <InputReadOnly
            label="How many staff do you employ in care roles?"
            value={formValues.staffCount || ""}
          />

          <SectionHeaderWithEdit title="Age Range" stepToNavigate={steps.SUPPORT} />

          <div className={styles.formGroup}>
            <InputReadOnly
              label="Minimum child age (years)"
              value={formValues.minAgeInYears || ""}
            />
            <InputReadOnly
              label="Maximum child age (years)"
              value={formValues.maxAgeInYears || ""}
            />
          </div>

          <SectionHeaderWithEdit title="Type of Care" stepToNavigate={steps.SUPPORT} />

          <InputReadOnly
            label="Type of care you offer"
            value={
              formValues.typeOfCare && formValues.typeOfCare !== "-1"
                ? TYPE_OF_CARE[formValues.typeOfCare] || ""
                : ""
            }
          />

          <SectionHeaderWithEdit title="Genders" stepToNavigate={steps.SUPPORT} />

          <InputReadOnly
            label="Genders accepted"
            value={genderLabels.length > 0 ? genderLabels.join(", ") : "None selected"}
          />

          {selectedSupportNeeds.length > 0 && (
            <>
              <SectionHeaderWithEdit title="Support Available" stepToNavigate={steps.SUPPORT} />

              {selectedSupportNeeds.map((category) => (
                <div key={category.category} style={{ marginBottom: 20 }}>
                  <h4 className="h6" style={{ marginBottom: 10, fontWeight: 600 }}>
                    {category.category}
                  </h4>
                  <InputReadOnly label="" value={category.items.join(", ")} />
                </div>
              ))}
            </>
          )}

          <SectionHeaderWithEdit title="Availability" stepToNavigate={steps.AVAILABILITY} />

          <InputReadOnly label="Total care capacity" value={formValues.capacityMax || ""} />
          <InputReadOnly
            label="Current number of children in care"
            value={formValues.capacityUsed || ""}
          />

          <SectionHeaderWithEdit title="Gallery" stepToNavigate={steps.GALLERY} />

          <InputReadOnly
            label="Gallery images/videos"
            value={
              galleryURLs?.length > 0
                ? `${galleryURLs.length} file${galleryURLs.length !== 1 ? "s" : ""} uploaded`
                : "No files uploaded"
            }
          />

          <SectionHeaderWithEdit title="Documents" stepToNavigate={steps.DOCUMENT} />

          <InputReadOnly
            label="Documents"
            value={
              documents?.length > 0
                ? `${documents.length} document${documents.length !== 1 ? "s" : ""} uploaded`
                : "No documents uploaded"
            }
          />
        </div>
      </div>
    );
  }

  function showGalleryForm() {
    return (
      <div className={styles.formSection}>
        {galleryURLs?.length > 0 ? (
          <div className={styles.galleryGrid}>
            {galleryURLs.map((media) => {
              if (media.type?.includes("image")) {
                return (
                  <div className={styles.galleryItem} key={media.externalId}>
                    <img
                      src={`${AWS_S3_BASE_URL}/${media.externalId}`}
                      alt="Gallery"
                    />
                    <div
                      className={styles.deleteButton}
                      onClick={() => {
                        deleteImage(media.externalId);
                      }}
                    >
                      <i
                        className={`bi bi-x-circle`}
                        style={{
                          fontSize: "14px",
                          color: "black",
                        }}
                      ></i>
                    </div>
                  </div>
                );
              } else if (media.type?.includes("video")) {
                return (
                  <div className={styles.galleryItem} key={media.externalId}>
                    <video controls>
                      <source src={`${AWS_S3_BASE_URL}/${media.externalId}`} type={media.type} />
                    </video>

                    <div
                      className={styles.deleteButton}
                      onClick={() => {
                        deleteImage(media.externalId);
                      }}
                    >
                      <i
                        className={`bi bi-x-circle`}
                        style={{
                          fontSize: "14px",
                          color: "black",
                        }}
                      ></i>
                    </div>
                  </div>
                );
              } else {
                console.error(`UNKNOWN mime type for location galleryURLs`);
                return null;
              }
            })}
          </div>
        ) : null}

        <InputWithLabel
          inputId={`fileUploadInput`}
          label={`Location videos and images`}
          type="file"
          inputProps={{
            accept: "image/*,video/*",
            multiple: true,
            onChange: handleSelectedGalleryImage,
          }}
          register={register}
        />
      </div>
    );
  }

  function showDocumentsForm() {
    return (
      <div className={styles.formSection}>
        {documents?.length > 0 ? (
          <div className={styles.documentGrid}>
            {documents.map((document) => {
              return (
                <a href={document.expiringUrl} target="_blank" key={document.externalId} style={{ textDecoration: 'none' }}>
                  <div className={styles.documentItem}>
                    <i
                      className={`bi ${getIconType(document.type)}`}
                      style={{
                        fontSize: "42px",
                        color: variables.purple,
                      }}
                    ></i>

                    <div className={styles.documentName}>{document.name}</div>

                    <div className={styles.documentSize}>
                      {document.size ? `${(document.size / 1024 / 1024).toFixed(1)}MB` : null}
                    </div>

                    <div
                      className={styles.deleteButton}
                      onClick={(event) => {
                        // Prevent default action by href
                        event.preventDefault();
                        deleteDocument(document.externalId);
                      }}
                    >
                      <i
                        className={`bi bi-x-circle`}
                        style={{
                          fontSize: "14px",
                          color: "black",
                        }}
                      ></i>
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        ) : null}

        <InputWithLabel
          inputId={`fileUploadDocuments`}
          label={`Documents`}
          type="file"
          inputProps={{
            accept:
              "application/pdf, application/msword, text/csv, application/vnd.openxmlformats-officedocument.wordprocessingml.document, text/html, application/json, application/vnd.oasis.opendocument.presentation, application/vnd.oasis.opendocument.text, application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.presentationml.presentation, text/plain, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/zip",

            multiple: true,
            onChange: handleSelectedDocument,
          }}
          register={register}
        />
      </div>
    );
  }

  function showAvailabilityForm() {
    return (
      <div className={styles.formSection}>
        <InputWithLabel
          inputId={`capacityMax`}
          label={`What is your total care capacity?`}
          placeholder=""
          type="number"
          register={register}
          error={errors.capacityMax}
          errorMessage={`Required. Enter a number between 1-200`}
          validation={{ required: true, minLength: 1, min: 1, max: 200 }}
        />
        <InputWithLabel
          inputId={`capacityUsed`}
          label={`How many children are you currently caring for?`}
          placeholder=""
          type="number"
          register={register}
          error={errors.capacityUsed}
          errorMessage={`Required. Enter a number between 0-200`}
          validation={{ required: true, minLength: 1, min: 0, max: 200 }}
        />
      </div>
    );
  }

  function showPaymentForm() {
    return (
      <div className={`${styles.formSection} ${styles.paymentSection}`}>
        {locationData?.location?.subscriptionActive ? (
          <>
            <div className={styles.paymentInfo}>
              This location currently has an active subscription.
              <br />
              <br />
              Looking for an invoice or need help?
              <br />
              Contact us by emailing{" "}
              <a href="mailto:info@bplaced.co.uk?Subject=Subscription%20Query">
                info@bplaced.co.uk
              </a>
              .
            </div>

            <div>
              <hr />

              <p className={styles.warningText}>
                <i>Warning</i>: An active subscription is required or this location will no longer
                show up in search results.
              </p>
              <Button onClick={cancelLocation} variant={"outline-danger"}>
                Cancel Subscription
              </Button>
            </div>
          </>
        ) : null}

        {!locationData?.location?.subscriptionActive ? (
          <>
            {/* <div className={styles.paymentInfo}>
              Each location requires a subscription before it can appear in search results.
              <br />
              You do not currently have a subscription active for this location.
              <br />
              <br />
              The subscription price is £40 per month and you can cancel online at any time.
              <br />
              <br />
              <i>Special Offer</i>
              <br />
              New locations are eligible to claim our welcome offer. This promotion will be
              automatically applied.
            </div> */}
            {/* <div style={{ marginTop: 15 }}>
              <Button 
                onClick={goToListingCheckout} 
                variant={"primary"}
                disabled={!locationId || locationId === "null" || locationId === "undefined"}
              >
                PAY BY CARD
              </Button>
              {(!locationId || locationId === "null" || locationId === "undefined") && (
                <div className={styles.paymentInfo} style={{ marginTop: 10, color: '#dc3545', fontSize: '0.9em' }}>
                  Please save your location information first before proceeding to payment.
                </div>
              )}
            </div>

            <hr /> */}
            {/* <div style={{ marginTop: 15 }}>
              <div className={styles.paymentInfo}>
                Alternatively we can also accept payment by invoice.
              </div>
              <Button
                onClick={() => {
                  console.log(`TODO Invoice`);
                }}
                variant={"outline-primary"}
              >
                PAY BY INVOICE
              </Button>
            </div> */}
          </>
        ) : null}
      </div>
    );
  }

  const getStepProgressPercent = useMemo(() => {
    if (step === steps.INFORMATION) {
      return 25;
    }

    if (step === steps.SUPPORT) {
      return 50;
    }

    if (step === steps.AVAILABILITY) {
      return 75;
    }

    return 90;
  }, [step]);

  return (
    <AuthenticatedWrapper user={user}>
      <NavigationBack />

      <Row className={styles.addLocationContainer} style={{ padding: "0px 0px 30px" }}>
        <Col xs={12} lg={9} xl={6}>
          <Nav variant="tabs" className={styles.formTabs}>
            <Nav.Item className={styles.navItem}>
              <Nav.Link
                className={
                  step === steps.INFORMATION ? styles.active + " " + styles.navLink : styles.navLink
                }
                onClick={() => {
                  console.log(`Information`);
                  setStep(steps.INFORMATION);
                }}
                active={step === steps.INFORMATION}
              >
                General
              </Nav.Link>
            </Nav.Item>
            <Nav.Item className={styles.navItem}>
              <Nav.Link
                className={
                  step === steps.SUPPORT ? styles.active + " " + styles.navLink : styles.navLink
                }
                onClick={() => {
                  console.log(`Support`);
                  setStep(steps.SUPPORT);
                }}
                active={step === steps.SUPPORT}
              >
                Suitability
              </Nav.Link>
            </Nav.Item>
            <Nav.Item className={styles.navItem}>
              <Nav.Link
                className={
                  step === steps.GALLERY ? styles.active + " " + styles.navLink : styles.navLink
                }
                onClick={() => {
                  console.log(`Gallery`);
                  setStep(steps.GALLERY);
                }}
                active={step === steps.GALLERY}
              >
                Gallery
              </Nav.Link>
            </Nav.Item>
            <Nav.Item className={styles.navItem}>
              <Nav.Link
                className={
                  step === steps.DOCUMENT ? styles.active + " " + styles.navLink : styles.navLink
                }
                onClick={() => {
                  console.log(`Documents`);
                  setStep(steps.DOCUMENT);
                }}
                active={step === steps.DOCUMENT}
              >
                Documents
              </Nav.Link>
            </Nav.Item>
            <Nav.Item className={styles.navItem}>
              <Nav.Link
                className={
                  step === steps.AVAILABILITY
                    ? styles.active + " " + styles.navLink
                    : styles.navLink
                }
                onClick={() => {
                  console.log(`Support`);
                  setStep(steps.AVAILABILITY);
                }}
                active={step === steps.AVAILABILITY}
              >
                Availability
              </Nav.Link>
            </Nav.Item>
            <Nav.Item className={styles.navItem}>
              <Nav.Link
                className={
                  step === steps.CONFIRMATION
                    ? styles.active + " " + styles.navLink
                    : styles.navLink
                }
                onClick={() => {
                  setStep(steps.CONFIRMATION);
                }}
                active={step === steps.CONFIRMATION}
              >
                Confirmation
              </Nav.Link>
            </Nav.Item>
          </Nav>
          <Card className={styles.formCard}>
            <Card.Body className={styles.cardBody}>
              <div className={styles.formHeader}>
                <h1 className="h3">
                  {locationData?.location?._id ? `Update Location` : `Confrim and submit Location`}
                </h1>
              </div>

              {/* Do not use onSubmit, will triger submit on enter, not wanted! */}
              <form>
                {
                  // Progress bar is only shown for NEW locations
                  !locationData?.location?._id ? (
                    <div className={styles.progressContainer}>
                      <div className={styles.progressBar}>
                        <div
                          className={styles.progressFill}
                          style={{ width: `${getStepProgressPercent}%` }}
                        />
                      </div>
                    </div>
                  ) : null
                }

                {step === steps.INFORMATION ? showGeneralForm() : null}

                {step === steps.SUPPORT ? showSupportForm() : null}

                {step === steps.GALLERY ? showGalleryForm() : null}

                {step === steps.DOCUMENT ? showDocumentsForm() : null}

                {step === steps.AVAILABILITY ? showAvailabilityForm() : null}

                {step === steps.CONFIRMATION ? showConfirmationForm() : null}

                {/* {step === steps.PAYMENT ? showPaymentForm() : null} */}

                {step === steps.CONFIRMATION ? (
                  <div style={{ marginTop: 30, paddingTop: 20, borderTop: "1px solid #e7eeee" }}>
                    <button type="button" className={styles.nextButton} onClick={saveAndSubmitLocation}>
                      {locationId ? "UPDATE" : "SAVE AND SUBMIT"}
                    </button>
                  </div>
                ) : null}

                {step !== steps.PAYMENT && step !== steps.CONFIRMATION ? (
                  <div style={{ marginTop: 30, paddingTop: 20, borderTop: "1px solid #e7eeee" }}>
                    <button type="button" className={styles.nextButton} onClick={nextStep}>
                      {locationId ? "UPDATE" : "NEXT"}
                    </button>
                  </div>
                ) : null}
              </form>
            </Card.Body>
          </Card>
        </Col>

        {locationStatus === null ? (
          <Col xs={12} lg={3} xl={4}>
            <Card className={`mobile-gap ${styles.infoCard}`}>
              <Card.Body className={styles.cardBody}>
                <h1 className="h3">Why list with bPlaced?</h1>

                <p>
                  List your accomodation with bPlaced and get found by local authorities who are
                  actively placing children in your area.
                </p>

                <h1 className="h5">Steps</h1>

                <p>
                  1. Complete the short form with key information, including adding any photos or
                  supporting documents.
                </p>
                {/* <p>2. Pay the bPlaced listing fee of £99/year by debit or credit card.</p> */}
                <p>
                  2. Your listing will go live as soon as it has been reviewed by our team. Listings
                  are normally reviewed within two business days.
                </p>
                <p>
                  3. Local authorities across the UK can now make an enquiry to place a child with
                  you! The enquiry email contains all of the key information in one place for you to
                  review and respond.
                </p>
                <p>
                  4. All finalised? After a child has been placed with you, remember to update your
                  availability as needed.
                </p>
              </Card.Body>
            </Card>
          </Col>
        ) : null}

        {locationStatus === IN_REVIEW && user?.staffUser === true ? (
          <Col xs={12} lg={3} xl={4}>
            <Card className={`mobile-gap ${styles.infoCard}`}>
              <Card.Body className={styles.cardBody}>
                <h1 className="h3" style={{ marginBottom: 15 }}>
                  Review Listing
                </h1>

                <p>
                  Please review the listing information and decide whether to approve or reject the
                  listing.
                </p>

                <p>
                  {createdByUser ? (
                    <>
                      Contact: <a href={`mailto:${createdByUser?.email}`}>{createdByUser?.email}</a>
                    </>
                  ) : null}
                  <br />
                  Created:{" "}
                  {isToday(locationData.location?.created)
                    ? "Today"
                    : format(locationData.location?.created, "dd/MM/yyyy")}
                </p>

                <Button
                  variant="outline-primary"
                  active={showSupportNeeds === false}
                  onClick={async () => {
                    console.log(`Approve`);
                    await axios.put(`${serverUrl}/location`, {
                      _id: locationData.location?._id,
                      status: APPROVED,
                    });

                    await mutate(`${serverUrl}/location`);
                    await mutate(`${serverUrl}/location?draft=true`);

                    if (typeof back === "string") {
                      navigate(back);
                    } else {
                      navigate(-1);
                    }
                  }}
                  style={{ marginRight: 5 }}
                >
                  APPROVE
                </Button>

                <Button
                  variant="outline-danger"
                  active={showSupportNeeds === false}
                  onClick={async () => {
                    console.log(`Reject`);
                    await axios.put(`${serverUrl}/location`, {
                      _id: locationData.location?._id,
                      status: REJECTED,
                    });

                    await mutate(`${serverUrl}/location`);
                    await mutate(`${serverUrl}/location?draft=true`);

                    if (typeof back === "string") {
                      navigate(back);
                    } else {
                      navigate(-1);
                    }
                  }}
                  style={{ marginLeft: 5 }}
                >
                  REJECT
                </Button>
              </Card.Body>
            </Card>
          </Col>
        ) : null}

        {/* {locationStatus === IN_REVIEW &&
        user?.staffUser !== true &&
        !locationData?.location?.subscriptionActive ? (
          <Col xs={12} lg={3} xl={4}>
            <Card className={`mobile-gap ${styles.infoCard}`}>
              <Card.Body className={styles.cardBody}>
                <h1 className="h3" style={{ marginBottom: 15 }}>
                  Subscription Required
                </h1>

                <p>
                  The final step is to purchase a subscription for this location. Securely pay
                  online by debit or credit card. Our team can then get started reviewing your
                  listing before it goes live!
                </p>

                <p className="text-decoration-underline" onClick={goToListingCheckout}>
                  Pay now
                </p>
              </Card.Body>
            </Card>
          </Col>
        ) : null} */}

        {locationStatus === IN_REVIEW &&
        user?.staffUser !== true &&
        locationData?.location?.subscriptionActive ? (
          <Col xs={12} lg={3} xl={4}>
            <Card className={`mobile-gap ${styles.infoCard}`}>
              <Card.Body className={styles.cardBody}>
                <h1 className="h3" style={{ marginBottom: 15 }}>
                  Listing In Review
                </h1>

                <p>
                  Thank you for submitting your location. It is now being reviewed by our team but
                  you can continue to edit as needed. We will update you by email once a decision
                  has been made.
                </p>
              </Card.Body>
            </Card>
          </Col>
        ) : null}

        {locationStatus === REJECTED ? (
          <Col xs={12} lg={3} xl={4}>
            <Card className={`mobile-gap ${styles.infoCard}`}>
              <Card.Body className={styles.cardBody}>
                <h1 className="h3" style={{ marginBottom: 15 }}>
                  Listing Rejected
                </h1>

                <p>
                  Your listing has been rejected. Please review our feedback and add or amend any
                  required fields.
                </p>
              </Card.Body>
            </Card>
          </Col>
        ) : null}

        {locationStatus === APPROVED ? (
          <Col xs={12} lg={3} xl={4}>
            <Card className={`mobile-gap ${styles.infoCard}`}>
              <Card.Body className={styles.cardBody}>
                <h1 className="h3" style={{ marginBottom: 15 }}>
                  Listing Live
                </h1>

                <p>
                  Your listing is now live! You can still add or update location information here at
                  any time.
                  <br />
                  Tip: It is important to keep availability up to date and this impacts our search
                  order.
                </p>
              </Card.Body>
            </Card>
          </Col>
        ) : null}
      </Row>

      <BasicModal
        title={modalConfig?.title}
        message={modalConfig?.message}
        show={showModal}
        close={hideModal}
        size="sm"
      />
    </AuthenticatedWrapper>
  );
}

export default AddLocation;
